<template>
  <component
    to="/copilot?trigger=true"
    :is="copilotIsDisplayed ? 'router-link' : 'div'"
    class="w-full cursor-pointer"
    @click="toggleExpanded"
  >
    <div
      class="w-full border border-gray-2 rounded-lg mayday-ai-gradient-light text-body"
    >
      <div class="flex p-2 px-3 items-center justify-between space-x-2 w-full">
        <div class="flex items-center space-x-2 text-sm">
          <FontAwesomeIcon icon="ticket" />
          <div>{{ $t(toastLabel) }}</div>
        </div>
        <FontAwesomeIcon
          :icon="toastIcon.icon"
          :spin="toastIcon.spin"
          class="transition-all"
          :class="expanded ? 'rotate-90' : 'rotate-0'"
        />
      </div>
      <div
        v-if="!copilotIsDisplayed"
        class="transition-all overflow-y-auto px-2"
        :class="expanded ? 'max-h-[300px] py-2' : 'max-h-0 py-0'"
        @click.stop
      >
        <CopilotAutomations
          :manual-automations-loading="manualAutomationsLoading"
          :manual-automations="manualAutomations"
          :brain-automations="brainAutomations"
          :brain-automations-loading="brainAutomationsLoading"
          :lang="selectedLanguage || locale"
          :display-brain-recommendations="hasCompanyBrainAutomationsPreference"
          :display-manual-recommendations="true"
          @go-to-content="handleGoToContent"
        />
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import {
  ExternalContent,
  useAdminStore,
  useCopilotStore,
  useKnowledgeStore,
} from '@getmayday/vue-modules';
import type { LightEntity } from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import CopilotAutomations from './CopilotAutomations.vue';
import { externalContentUri } from '../../utils/externalContentsUri';
import { useConfigStore } from '../../stores/config';

const adminStore = useAdminStore();
const {
  hasCompanyBrainAutomationsPreference,
  hasCompanyBrainAgentsPreference,
  hasCompanyBrainAIPreference,
} = storeToRefs(adminStore);

const copilotStore = useCopilotStore();
const {
  manualAutomations,
  manualAutomationsLoading,
  brainAutomations,
  brainAutomationsLoading,
} = storeToRefs(copilotStore);

const agentStore = useAgentStore();
const { availableAgents } = storeToRefs(agentStore);

const configStore = useConfigStore();
const { locale } = storeToRefs(configStore);

const knowledgeStore = useKnowledgeStore();
const { selectedLanguage } = storeToRefs(knowledgeStore);

const { goToEntity } = useEntities();

const expanded = ref(false);

const copilotIsDisplayed = computed(() => {
  return (
    hasCompanyBrainAgentsPreference.value &&
    hasCompanyBrainAIPreference.value &&
    availableAgents.value?.length
  );
});

const toggleExpanded = () => {
  if (copilotIsDisplayed.value) return;
  expanded.value = !expanded.value;
  copilotStore.updateHasUserOpenedCopilot(true);
};

const handleGoToContent = (content: LightEntity | ExternalContent) => {
  if (content.hasOwnProperty('type')) {
    goToEntity({
      ...(content as LightEntity),
      fromModal: true,
    });
  } else {
    window.open(externalContentUri(content as ExternalContent), '_blank');
  }
};

const toastIcon = computed(() => {
  if (copilotIsDisplayed.value || !manualAutomationsLoading.value) {
    return {
      icon: 'chevron-right',
      spin: false,
    };
  }
  return {
    icon: 'spinner-third',
    spin: true,
  };
});

const toastLabel = computed(() => {
  if (!copilotIsDisplayed.value) {
    return 'genius.context.recommended-content';
  }
  return 'genius.context.title';
});
</script>

<style scoped></style>
